import { m, useScroll } from 'framer-motion';
import { useEffect, useRef, useState, useCallback } from 'react';
// @mui
import { styled, alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import Button from '@mui/material/Button';

// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// theme
import { bgGradient, bgBlur } from 'src/theme/css';
// layouts
import { HEADER } from 'src/layouts/config-layout';
import { MotionContainer, varFade } from 'src/components/animate';

// utils
import { restaurantStats, supplierStats } from '../../utils/data';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  ...bgGradient({
    color: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.9 : 0.94),
    imgUrl: '/assets/background/overlay_3.jpg',
  }),
  width: '100%',
  height: '100vh',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    top: 0,
    left: 0,
    position: 'fixed',
  },
}));

const StyledWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    marginTop: HEADER.H_DESKTOP_OFFSET,
  },
}));

const StyledEllipseTop = styled('div')(({ theme }) => ({
  top: -80,
  width: 480,
  right: -80,
  height: 480,
  borderRadius: '50%',
  position: 'absolute',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.12),
}));

const StyledEllipseBottom = styled('div')(({ theme }) => ({
  height: 400,
  bottom: -200,
  left: '10%',
  right: '10%',
  borderRadius: '50%',
  position: 'absolute',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.12),
}));

const StyledPolygon = styled('div')(({ opacity = 1, anchor = 'left', theme }) => ({
  ...bgBlur({
    opacity,
    color: theme.palette.background.default,
  }),
  zIndex: 9,
  bottom: 0,
  height: 80,
  width: '50%',
  position: 'absolute',
  clipPath: 'polygon(0% 0%, 100% 100%, 0% 100%)',
  ...(anchor === 'left' && {
    left: 0,
  }),
  ...(anchor === 'right' && {
    right: 0,
    transform: 'scaleX(-1)',
  }),
}));

// ----------------------------------------------------------------------

export default function HomeHero() {
  const mdUp = useResponsive('up', 'md');
  const theme = useTheme();
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const [percent, setPercent] = useState(0);
  const [selectedRole, setSelectedRole] = useState(null);

  const getScroll = useCallback(() => {
    let heroHeight = 0;

    if (heroRef.current) {
      heroHeight = heroRef.current.offsetHeight;
    }

    scrollY.on('change', (scrollHeight) => {
      const scrollPercent = (scrollHeight * 100) / heroHeight;

      setPercent(Math.floor(scrollPercent));
    });
  }, [scrollY]);

  useEffect(() => {
    getScroll();
  }, [getScroll]);

  const transition = {
    repeatType: 'loop',
    ease: 'linear',
    duration: 60 * 4,
    repeat: Infinity,
  };

  const opacity = 1 - percent / 100;
  const hide = percent > 120;

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const renderRoleContent = () => {
    if (selectedRole === 'restaurant') {
      return (
        <Stack spacing={3} sx={{ textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            For Restaurants
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Streamline your procurement process with our comprehensive platform designed
            specifically for food service businesses.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {restaurantStats.map((stat, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  p: 2,
                  bgcolor: 'background.neutral',
                  borderRadius: 2,
                  minWidth: 120,
                }}
              >
                <Typography variant="h6" sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption">{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      );
    }

    if (selectedRole === 'supplier') {
      return (
        <Stack spacing={3} sx={{ textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
          <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
            For Suppliers
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Expand your reach and streamline operations with our platform that connects you directly
            to restaurants and food service businesses.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {supplierStats.map((stat, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  p: 2,
                  bgcolor: 'background.neutral',
                  borderRadius: 2,
                  minWidth: 120,
                }}
              >
                <Typography variant="h6" sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption">{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      );
    }

    return null;
  };

  const renderDescription = (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        height: 1,
        mx: 'auto',
        maxWidth: 600,
        opacity: opacity > 0 ? opacity : 0,
        mt: {
          md: `-${HEADER.H_DESKTOP + percent * 2.5}px`,
        },
        width: '100%',
        textAlign: 'center',
        px: { xs: 2, sm: 0 },
      }}
    >
      {!selectedRole ? (
        <>
          <m.div variants={varFade().in}>
            <Typography
              variant="h1"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                lineHeight: 1.2,
                width: '100%',
                px: { xs: 1, sm: 0 },
              }}
            >
              Streamline Your
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                F&B Supply Chain
              </Box>
            </Typography>
          </m.div>

          <m.div variants={varFade().in}>
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                mb: 4,
                color: 'text.secondary',
                maxWidth: '500px',
                width: '100%',
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              Connect directly with suppliers, save time, and optimize your procurement process
            </Typography>
          </m.div>

          <m.div variants={varFade().in}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                px: { xs: 2, sm: 0 },
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => handleRoleSelect('restaurant')}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  minWidth: { xs: '180px', sm: '200px' },
                  maxWidth: { xs: '260px', sm: 'none' },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                }}
              >
                I&apos;m a Restaurant
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleRoleSelect('supplier')}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  minWidth: { xs: '180px', sm: '200px' },
                  maxWidth: { xs: '260px', sm: 'none' },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                }}
              >
                I&apos;m a Supplier
              </Button>
            </Stack>
          </m.div>
        </>
      ) : (
        <m.div variants={varFade().in}>
          {renderRoleContent()}
          <Button
            variant="outlined"
            size="medium"
            onClick={() => setSelectedRole(null)}
            sx={{
              mt: 3,
              borderColor: 'text.secondary',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'text.primary',
                color: 'text.primary',
              },
            }}
          >
            ← Back to Overview
          </Button>
        </m.div>
      )}
    </Stack>
  );

  const renderSlides = (
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={{
        height: '150%',
        position: 'absolute',
        opacity: opacity > 0 ? opacity : 0,
        // transform: `skew(${-16 - percent / 24}deg, ${4 - percent / 16}deg)`,
      }}
    >
      <Stack
        component={m.div}
        variants={varFade().in}
        sx={{
          width: 344,
          position: 'relative',
        }}
      >
        <Box
          component={m.img}
          animate={{ y: ['0%', '100%'] }}
          transition={transition}
          alt="light_1"
          src="/assets/images/home/hero/home_2.png"
          sx={{ position: 'absolute', mt: -5 }}
        />
        <Box
          component={m.img}
          animate={{ y: ['-100%', '0%'] }}
          transition={transition}
          alt="light_1"
          src="/assets/images/home/hero/home_2.png"
          sx={{ position: 'absolute' }}
        />
      </Stack>

      <Stack
        component={m.div}
        variants={varFade().in}
        sx={{ width: 344, position: 'relative', ml: -5 }}
      >
        <Box
          component={m.img}
          animate={{ y: ['100%', '0%'] }}
          transition={transition}
          alt="light_2"
          src="/assets/images/home/hero/home_3.png"
          sx={{ position: 'absolute', mt: -5 }}
        />
        <Box
          component={m.img}
          animate={{ y: ['0%', '-100%'] }}
          transition={transition}
          alt="light_2"
          src="/assets/images/home/hero/home_3.png"
          sx={{ position: 'absolute' }}
        />
      </Stack>
    </Stack>
  );

  const renderPolygons = (
    <>
      <StyledPolygon />
      <StyledPolygon anchor="right" opacity={0.48} />
      <StyledPolygon anchor="right" opacity={0.48} sx={{ height: 48, zIndex: 10 }} />
      <StyledPolygon anchor="right" sx={{ zIndex: 11, height: 24 }} />
    </>
  );

  const renderEllipses = (
    <>
      {mdUp && <StyledEllipseTop />}
      <StyledEllipseBottom />
    </>
  );

  return (
    <>
      <StyledRoot
        ref={heroRef}
        sx={{
          ...(hide && {
            opacity: 0,
          }),
        }}
      >
        <StyledWrapper>
          <Container component={MotionContainer} sx={{ height: 1 }}>
            <Grid container columnSpacing={{ md: 10 }} sx={{ height: 1 }}>
              <Grid xs={12} md={6}>
                {renderDescription}
              </Grid>

              {mdUp && <Grid md={6}>{renderSlides}</Grid>}
            </Grid>
          </Container>

          {renderEllipses}
        </StyledWrapper>
      </StyledRoot>

      {mdUp && renderPolygons}

      <Box sx={{ height: { md: '100vh' } }} />
    </>
  );
}
